import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { buildApiUrl } from '../config/api';

export const useInvestments = () => {
  const { getAuthHeaders, user } = useAuth();
  const { isDemoModeActive, getDemoQueryParams } = useDemo();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all investments
  const fetchInvestments = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const queryParams = getDemoQueryParams();
      const response = await fetch(buildApiUrl(`/api/investment${queryParams}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      // Handle non-OK responses
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to fetch investments';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        setInvestments(result.data || []);
      } else {
        throw new Error(result.error || 'Failed to fetch investments');
      }
    } catch (err) {
      console.error('Error fetching investments:', err);
      setError(err.message || 'Failed to fetch investments');
      setInvestments([]);
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, getDemoQueryParams]);

  // Create new investment
  const createInvestment = useCallback(async (investmentData) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      const response = await fetch(buildApiUrl('/api/investment'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(investmentData),
      });

      // Handle 404 - backend endpoint doesn't exist yet
      if (response.status === 404) {
        console.warn('Investments API endpoint not found - backend not implemented yet');
        // For now, just add to local state for UI testing
        setInvestments(prev => [...prev, { ...investmentData, id: investmentData.id || Date.now().toString() }]);
        return investmentData;
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to create investment';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        await fetchInvestments(); // Refresh the list
        // Return full result to include assetCreated info
        return {
          ...result.data,
          assetCreated: result.assetCreated || false,
          createdAssetType: result.createdAssetType || null,
          message: result.message || 'Investment created successfully'
        };
      } else {
        throw new Error(result.error || 'Failed to create investment');
      }
    } catch (err) {
      console.error('Error creating investment:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchInvestments, isDemoModeActive]);

  // Update existing investment
  const updateInvestment = useCallback(async (id, investmentData) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      const response = await fetch(buildApiUrl(`/api/investment/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(investmentData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to update investment';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        await fetchInvestments(); // Refresh the list
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to update investment');
      }
    } catch (err) {
      console.error('Error updating investment:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchInvestments, isDemoModeActive]);

  // Delete investment
  const deleteInvestment = useCallback(async (id) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      const response = await fetch(buildApiUrl(`/api/investment/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to delete investment';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        await fetchInvestments(); // Refresh the list
        return true;
      } else {
        throw new Error(result.error || 'Failed to delete investment');
      }
    } catch (err) {
      console.error('Error deleting investment:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchInvestments, isDemoModeActive]);

  // Calculate summary statistics
  const calculateSummary = useCallback(() => {
    const currentYear = new Date().getFullYear();
    
    // Calculate total annual investment
    const totalAnnualInvestment = investments.reduce((sum, inv) => {
      if (!inv.isActive) return sum;
      if (inv.startYear > currentYear || (inv.endYear && inv.endYear < currentYear)) return sum;
      
      const freq = inv.frequency || 'monthly';
      const amount = inv.amount || 0;
      let annualAmount = 0;
      
      if (freq === 'monthly') annualAmount = amount * 12;
      else if (freq === 'quarterly') annualAmount = amount * 4;
      else if (freq === 'annually') annualAmount = amount;
      else if (freq === 'one-time') annualAmount = currentYear === inv.startYear ? amount : 0;
      
      return sum + annualAmount;
    }, 0);

    // Group by asset class
    const byAssetClass = investments.reduce((acc, inv) => {
      if (!inv.isActive) return acc;
      const type = inv.type || 'Other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(inv);
      return acc;
    }, {});

    // Calculate average expected return
    const activeInvestments = investments.filter(inv => inv.isActive);
    const avgExpectedReturn = activeInvestments.length > 0
      ? activeInvestments.reduce((sum, inv) => sum + (inv.expectedReturn || 0), 0) / activeInvestments.length
      : 0;

    // Count by frequency
    const byFrequency = investments.reduce((acc, inv) => {
      if (!inv.isActive) return acc;
      const freq = inv.frequency || 'monthly';
      acc[freq] = (acc[freq] || 0) + 1;
      return acc;
    }, {});

    return {
      totalInvestments: investments.length,
      activeInvestments: activeInvestments.length,
      totalAnnualInvestment,
      avgExpectedReturn: Math.round(avgExpectedReturn * 100) / 100,
      byAssetClass,
      byFrequency,
      assetClasses: Object.keys(byAssetClass)
    };
  }, [investments]);

  // Load investments on mount and when user or demo mode changes
  useEffect(() => {
    if (user) {
      fetchInvestments();
    } else {
      setInvestments([]);
      setLoading(false);
    }
  }, [user, fetchInvestments, isDemoModeActive]);

  return {
    investments,
    loading,
    error,
    createInvestment,
    updateInvestment,
    deleteInvestment,
    calculateSummary,
    refreshInvestments: fetchInvestments,
  };
};

